import React, { useMemo, useState } from 'react';

import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import cx from 'classnames';
import { ASSIGNMENT_CREATE_MODES } from 'constants/assignments';

import { ReactComponent as DocumentIcon } from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/assets/document.svg';
import ClassSelector from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/ClassSelector';
import ExemplarUpload from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/ExemplarUpload';
import RubricSelector from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/RubricSelector';
import StandardSelector from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/StandardSelector';
import TemplateDropdown from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/TemplateDropdown';
import {
  assignmentAssign,
  useRecentSectionsList
} from 'pages/Teachers/shared/AssignmentDialog/components/AssignAssignment/sdk';
import { VALUES_OPTIONS } from 'pages/Teachers/shared/AssignmentDialog/constants';
import { trackerCreate } from 'pages/Teachers/shared/AssignmentDialog/sdk';
import {
  buildAssignmentFormData,
  validateAssignmentForm
} from 'utils/assignments';
import { notifyError } from 'utils/notifications';

import styles from './styles.module.scss';

const MathAssignmentForm = ({ mode, setMode, onCreationSuccess }) => {
  const [assignmentName, setAssignmentName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedRubric, setSelectedRubric] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const { data: rawSectionsResponse, isLoading: isLoadingSections } =
    useRecentSectionsList();

  const isFormValid = validateAssignmentForm({
    mode,
    assignmentName,
    description,
    selectedSections,
    selectedRubric,
    selectedStandards,
    uploadedFile
  });

  const processRecentSections = ({ nestedData }) => {
    if (!Array.isArray(nestedData)) return [];

    const allSections = [];

    nestedData.forEach((subArray) => {
      if (Array.isArray(subArray)) {
        subArray.forEach((section) => {
          if (section) allSections.push(section);
        });
      }
    });

    const uniqueSections = Array.from(
      new Map(allSections.map((item) => [item.id, item])).values()
    );

    return uniqueSections;
  };

  const sections = useMemo(() => {
    const actualData = Array.isArray(rawSectionsResponse)
      ? rawSectionsResponse
      : rawSectionsResponse?.data || [];

    return processRecentSections({ nestedData: actualData });
  }, [rawSectionsResponse]);

  const handleCreateAssignment = async () => {
    setIsSubmitting(true);

    try {
      const formData = buildAssignmentFormData({
        assignmentName,
        description,
        uploadedFile,
        selectedRubric,
        selectedStandards
      });

      const {
        success: createSuccess,
        data: createData,
        errors: createErrors
      } = await trackerCreate(formData);

      if (!createSuccess) notifyError(createErrors);

      // TODO: Replace with the logic about selecting the date
      const now = new Date();
      const due = new Date();
      due.setDate(now.getDate() + 2);

      const assignmentPayload = {
        sections_data: selectedSections.map((section) => ({
          section_id: section.id,
          open: now.toISOString(),
          due: due.toISOString()
        }))
      };

      const { success: assignSuccess, errors: assignErrors } =
        await assignmentAssign(createData.id, assignmentPayload);

      if (!assignSuccess) notifyError(assignErrors);

      onCreationSuccess({ createdTrackerId: createData.id });
    } catch (error) {
      notifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    setMode((prev) =>
      prev === ASSIGNMENT_CREATE_MODES.TEMPLATE
        ? ASSIGNMENT_CREATE_MODES.SCRATCH
        : ASSIGNMENT_CREATE_MODES.TEMPLATE
    );
    setSelectedTemplate(null);
    setUploadedFile(null);
  };

  const handleTemplateSelection = (template) => {
    setSelectedTemplate(template);
    if (template) {
      setAssignmentName(template.assignment_template_title);
    }
  };

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.container}>
        {/* --- HEADER --- */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>Launch New Assignment</h3>
          </div>

          <div className={styles.assignmentTypeRow}>
            <div className={styles.typeLabel}>
              <DocumentIcon />
              <span>Assignment</span>
            </div>
            <button onClick={toggleMode} className={styles.modeToggle}>
              {mode === ASSIGNMENT_CREATE_MODES.TEMPLATE
                ? 'Create from scratch →'
                : 'Use a Template →'}
            </button>
          </div>
        </div>

        {/* --- FORM CONTENT --- */}
        <div className={styles.formContent}>
          {/* Template Search (Template Mode Only) */}
          {mode === ASSIGNMENT_CREATE_MODES.TEMPLATE && (
            <TemplateDropdown
              selectedTemplate={selectedTemplate}
              onSelectTemplate={handleTemplateSelection}
            />
          )}

          <input
            type="text"
            className={styles.inputField}
            placeholder="Assignment Name"
            value={assignmentName}
            onChange={(e) => setAssignmentName(e.target.value)}
          />

          <textarea
            className={cx(styles.inputField, styles.textArea)}
            placeholder="Add a Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />

          {/* Learning Standard (Scratch Mode Only) */}
          {mode === ASSIGNMENT_CREATE_MODES.SCRATCH && (
            <StandardSelector
              selectedStandards={selectedStandards}
              setSelectedStandards={setSelectedStandards}
            />
          )}

          <ClassSelector
            sections={sections}
            selectedSections={selectedSections}
            setSelectedSections={setSelectedSections}
            isLoading={isLoadingSections}
          />

          <RubricSelector
            options={VALUES_OPTIONS}
            selected={selectedRubric}
            onSelect={setSelectedRubric}
          />

          {mode === ASSIGNMENT_CREATE_MODES.SCRATCH && (
            <ExemplarUpload file={uploadedFile} setFile={setUploadedFile} />
          )}
        </div>

        {/* --- FOOTER --- */}
        <div className={styles.footer}>
          <button
            className={styles.createButton}
            disabled={!isFormValid || isSubmitting}
            onClick={handleCreateAssignment}
          >
            {isSubmitting ? (
              'Processing...'
            ) : (
              <>
                <span></span>
                CREATE ASSIGNMENT
                <ArrowForwardIcon />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MathAssignmentForm;
